import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Timer, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const SUBJECTS = ["Order Query", "Clinical Data", "Wholesale", "Media"];

const FAQS = [
  { q: "How fast is dispatch?", a: "All orders leave our facility within 24 hours via cold-chain courier with tracking." },
  { q: "Are your peptides third-party tested?", a: "Yes. Every batch is verified by an ISO/IEC 17025 accredited laboratory. Each product ships with a Certificate of Analysis you can view on its COA page." },
  { q: "Do you ship internationally?", a: "We ship to most research jurisdictions. Import compliance remains the responsibility of the purchaser." },
];

const field =
  "w-full rounded-lg border border-white/10 bg-[#141417] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-200 focus:border-[#E8E9EB]/60";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry sent. Check your inbox for confirmation.");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    } catch {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="spotlight mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8" data-testid="contact-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">SUPPORT CHANNEL</div>
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="contact-heading">
          Contact & Support
        </h1>
      </motion.div>

      <div className="mt-14 grid gap-14 lg:grid-cols-[1fr_1.2fr]">
        <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
          <div className="flex flex-col gap-6">
            {[
              { icon: Mail, label: "EMAIL", value: "alphapepsaus@gmail.com", tid: "contact-email" },
              { icon: MapPin, label: "FACILITY", value: "Sydney NSW, Australia", tid: "contact-address" },
            ].map((c) => (
              <div key={c.label} className="glass flex items-center gap-4 rounded-xl p-5" data-testid={c.tid}>
                <c.icon className="h-5 w-5 shrink-0 text-[#E8E9EB]" />
                <div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">{c.label}</div>
                  <div className="mt-1 text-sm text-white">{c.value}</div>
                </div>
              </div>
            ))}
            <div className="glass flex items-center gap-4 rounded-xl border-[#6E7683]/40 p-5" data-testid="response-sla">
              <Timer className="h-5 w-5 shrink-0 text-[#E8E9EB]" />
              <div>
                <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">RESPONSE GUARANTEE</div>
                <div className="mt-1 text-sm text-white">Under 2 business hours, Monday to Saturday</div>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">FREQUENT QUESTIONS</div>
            <div className="mt-5 flex flex-col gap-3">
              {FAQS.map((f, i) => (
                <details key={f.q} className="group glass rounded-xl p-5" data-testid={`faq-item-${i}`}>
                  <summary className="cursor-pointer list-none text-sm font-medium text-white transition-colors duration-200 group-open:text-[#E8E9EB]">
                    {f.q}
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          onSubmit={submit}
          className="glass h-fit rounded-2xl p-8"
          data-testid="contact-form"
        >
          <h2 className="font-display text-xl font-semibold text-white">Send an enquiry</h2>
          <div className="mt-6 flex flex-col gap-4">
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} data-testid="contact-name-input" />
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} data-testid="contact-email-input" />
            <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={field} data-testid="contact-subject-select">
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <textarea required rows={5} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} data-testid="contact-message-input" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#6E7683] py-3.5 font-mono text-xs tracking-[0.25em] text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:opacity-50"
          >
            <Send className="h-3.5 w-3.5" /> {sending ? "SENDING..." : "SEND ENQUIRY"}
          </button>
          <p className="mt-3 text-center font-mono text-[10px] tracking-[0.1em] text-slate-600">
            A CONFIRMATION EMAIL IS SENT TO YOUR INBOX
          </p>
        </motion.form>
      </div>
    </div>
  );
}
